import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'

import { track } from '../../../analytics'
import { BASE_URL } from '../../../api/client'
import { TOKENS } from '../../ui/dashboard-primitives'
import {
  ARTIFACT_CHIP_AMBER,
  ARTIFACT_CHIP_NEUTRAL,
  ARTIFACT_EYEBROW,
  ARTIFACT_CARD_LINK,
  ARTIFACT_SURFACE,
  ARTIFACT_TITLE,
} from './styles'
import type { ExerciseArtifactData } from './types'

// Media bleeds to the card edge, so this shell drops ArtifactCard's padding
// and takes the surface directly. Spreading ARTIFACT_SURFACE and zeroing the
// padding reproduces V3's media card exactly — radius 14, the dark well wash,
// the 0.09 outline, no shadow and no width cap. Do not re-type those values.
const SHELL: CSSProperties = {
  ...ARTIFACT_SURFACE,
  display: 'grid',
  gridTemplateColumns: '96px 1fr',
  padding: 0,
  overflow: 'hidden',
  textDecoration: 'none',
}

const MEDIA: CSSProperties = {
  display: 'block',
  position: 'relative',
  minHeight: 96,
  background:
    `radial-gradient(80% 80% at 50% 40%, ${TOKENS.creamBorder}, transparent), rgba(28, 24, 22, 0.4)`,
}

const IMAGE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: 8,
  boxSizing: 'border-box',
}

/**
 * `exercise` — the Knowledge Base card: station photo, movement name, and the
 * muscles it loads.
 *
 * Follows MachineCard's pattern (BASE_URL prefix, onError fallback held in
 * state, contain-fit on a radial plate) rather than importing it: MachineCard
 * takes a TechnogymDeviceOut and renders usage stats no artifact carries.
 *
 * `image_url` arrives already prefixed with "/media" — the crawlers bake that
 * in at write time. Prefix BASE_URL and nothing else; a second "/media" 404s
 * every photo that actually exists.
 */
export function ExerciseArtifactCard({ data }: { data: ExerciseArtifactData }) {
  const [imageFailed, setImageFailed] = useState(false)
  const imageSrc = data.image_url && !imageFailed ? `${BASE_URL}${data.image_url}` : null

  const body = (
    <>
      <span style={MEDIA}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            loading="lazy"
            onError={() => setImageFailed(true)}
            style={IMAGE}
          />
        ) : null}
      </span>
      <span className="flex flex-col justify-center gap-1.5" style={{ padding: '14px 18px' }}>
        <span style={ARTIFACT_EYEBROW}>Exercise · Knowledge Base</span>
        <span style={{ ...ARTIFACT_TITLE, fontSize: 17 }}>{data.name}</span>
        {data.muscles.length > 0 ? (
          <span className="flex flex-wrap gap-1.5">
            {data.muscles.map((muscle, index) => (
              <span
                key={`${muscle}-${index}`}
                style={index < data.primary_count ? ARTIFACT_CHIP_AMBER : ARTIFACT_CHIP_NEUTRAL}
              >
                {muscle}
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </>
  )

  if (data.href) {
    return (
      <Link
        to={data.href}
        // Analytics (#708): the card is its own link, so it reports its own opening (the type, nothing it shows).
        onClick={() => track('coach_card_opened', { card_type: 'exercise' })}
        style={SHELL}
        className={`relative ${ARTIFACT_CARD_LINK}`}
        aria-label={`Knowledge Base: ${data.name}`}
      >
        {body}
      </Link>
    )
  }
  return <div style={SHELL}>{body}</div>
}
